import React from 'react';
import { DefaultButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { Callout, DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { Calendar, DayOfWeek } from 'office-ui-fabric-react/lib/Calendar';
import { FocusTrapZone } from 'office-ui-fabric-react/lib/FocusTrapZone';
import { useBoolean } from '@uifabric/react-hooks';
import PropTypes from 'prop-types';
import styles from './CalendarPicker.style';

const DayPickerStrings = {
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  goToToday: 'Go to today',
  weekNumberFormatString: 'Week number {0}',
  prevMonthAriaLabel: 'Previous month',
  nextMonthAriaLabel: 'Next month',
  prevYearAriaLabel: 'Previous year',
  nextYearAriaLabel: 'Next year',
  prevYearRangeAriaLabel: 'Previous year range',
  nextYearRangeAriaLabel: 'Next year range',
  closeButtonAriaLabel: 'Close',
  monthPickerHeaderAriaLabel: '{0}, select to change the year',
  yearPickerHeaderAriaLabel: '{0}, select to change the month',
};

const CalendarPicker = ({
  label,
  showMonthPickerAsOverlay,
  isDayPickerVisible,
  isMonthPickerVisible,
  showGoToToday,
  buttonString,
  highlightCurrentMonth,
  highlightSelectedMonth,
  selectedDate,
  onSelectDate,
  maxDate,
}) => {
  const [showCalendar, { toggle: toggleShowCalendar }] = useBoolean(false);

  return (
    <div data-testid="calender-picker" className={`${styles.container} calender-picker`}>
      <div className={styles.buttonContainer}>
        <Label>{label}</Label>
        <DefaultButton
          className={styles.innerButtonClass}
          data-testid="calender-picker-button"
          onClick={toggleShowCalendar}
          text={
            !selectedDate
              ? buttonString
              : `${selectedDate.getUTCMonth() + 1}/${selectedDate.getUTCFullYear()}`
          }
        />
      </div>
      <div className="ms-Button-flexContainer">
        <div className={styles.eraseButtonContainer}>
          <IconButton
            data-testid="calender-picker-erase-button"
            disabled={!selectedDate}
            onClick={() => {
              onSelectDate();
            }}
            iconProps={{ iconName: 'EraseTool' }}
            title="Clear Date"
            ariaLabel="Clear Date"
            styles={{ verticalAlign: 'bottom' }}
          />
        </div>
      </div>

      {showCalendar && (
        <Callout
          title="calender-picker-callout"
          label="Period"
          ariaLabel="Period"
          isBeakVisible={false}
          className="ms-DatePicker-callout"
          doNotLayer={false}
          directionalHint={DirectionalHint.bottomLeftEdge}
          target={`.${styles.buttonContainer}`}
          onDismiss={toggleShowCalendar}
          setInitialFocus
        >
          <FocusTrapZone
            firstFocusableSelector="ms-DatePicker-day--today"
            isClickableOutsideFocusTrap
          >
            <Calendar
              data-testid="calendar-picker-control"
              onSelectDate={date => {
                onSelectDate(date);
                toggleShowCalendar();
              }}
              className={styles.calendarHighlight}
              onDismiss={toggleShowCalendar}
              isMonthPickerVisible={isMonthPickerVisible}
              value={selectedDate}
              maxDate={maxDate}
              firstDayOfWeek={DayOfWeek.Sunday}
              strings={DayPickerStrings}
              isDayPickerVisible={isDayPickerVisible}
              highlightCurrentMonth={highlightCurrentMonth}
              highlightSelectedMonth={highlightSelectedMonth}
              showMonthPickerAsOverlay={showMonthPickerAsOverlay}
              showGoToToday={showGoToToday}
            />
          </FocusTrapZone>
        </Callout>
      )}
    </div>
  );
};

CalendarPicker.propTypes = {
  label: PropTypes.string,
  showMonthPickerAsOverlay: PropTypes.bool,
  isDayPickerVisible: PropTypes.bool,
  isMonthPickerVisible: PropTypes.bool,
  showGoToToday: PropTypes.bool,
  buttonString: PropTypes.string,
  highlightCurrentMonth: PropTypes.bool,
  highlightSelectedMonth: PropTypes.bool,
  selectedDate: PropTypes.instanceOf(Date),
  onSelectDate: PropTypes.func,
  maxDate: PropTypes.instanceOf(Date),
};

CalendarPicker.defaultProps = {
  label: 'Period',
  showMonthPickerAsOverlay: false,
  isDayPickerVisible: false,
  isMonthPickerVisible: true,
  showGoToToday: false,
  buttonString: 'Select Period',
  highlightCurrentMonth: false,
  highlightSelectedMonth: false,
  selectedDate: null,
  onSelectDate: null,
  maxDate: new Date(),
};

export default CalendarPicker;
